/**
 * Config сайта v2 по полям §3.7 (01.11, ТЗ 1.17 EMB-019 ч. 1; модель — widgetCfg.ts): каждая строка — одно верхнее поле.
 * «Сохранить» — PUT /api/widget-sites/:id/v2/config с config_version, который отдал GET (параллельная правка — 409 «обновите»).
 * Схему, хосты ссылок (в том числе запрет хоста прокладки, ТЗ п. 69(1)) и предел доменов проверяет сервер: отказ — 400 с полем,
 * строка подсвечивается. Предупреждения TXT-002 сохранение не останавливают — показываются после сохранения по полям.
 * Правка доходит до виджета за ≤ 5 мин (кэш прокладки и виджета, ТЗ §8.4). Перечитывание сайта после другого действия карточки
 * (текст согласия, вкл/выкл, перевыпуск, «Обновить») несохранённую правку не стирает (nextDraft); пустое обязательное поле выбора
 * показывает «— выберите —», а не первый пункт (ревью b235d4e4).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../api/client";
import { ApiException } from "../../../api/types";
import { BASE, failText } from "./widgetApi";
import { CFG_FIELDS, changedKeys, fieldOwner, fromDraft, nextDraft, sameDraft, selectOptions, toDraft, type CfgField, type Draft } from "./widgetCfg";
import type { Obj, TxtWarning } from "./widgetTypes";
import css from "./widget.module.css";

type Fault = { field: string; why: string } | null;

function Input({ f, value, onChange }: { f: CfgField; value: string; onChange: (v: string) => void }) {
  if (f.kind === "bool" || f.kind === "enum") {
    return (
      <select className={css.select} value={value} aria-label={f.label} onChange={(e) => onChange(e.target.value)}>
        {selectOptions(f, value).map((o) => <option key={o.value} value={o.value} disabled={o.disabled}>{o.label}</option>)}
      </select>
    );
  }
  const rows = f.kind === "json" ? Math.min(14, Math.max(3, value.split("\n").length)) : f.kind === "lines" ? 3 : 1;
  return f.kind === "int"
    ? <input className={css.input} value={value} aria-label={f.label} placeholder={f.hint} inputMode="numeric" onChange={(e) => onChange(e.target.value)} />
    : <textarea className={css.area} value={value} aria-label={f.label} placeholder={f.hint} rows={rows} spellCheck={false} onChange={(e) => onChange(e.target.value)} />;
}

export function WidgetSiteConfig({ id, config, version, onSaved }: { id: number; config: Obj | null; version: number; onSaved: () => void }) {
  const base = useMemo(() => toDraft(config), [config]);
  const [draft, setDraft] = useState<Draft>(base);
  const [fault, setFault] = useState<Fault>(null);
  const [warnings, setWarnings] = useState<TxtWarning[]>([]);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const prev = useRef(base), saved = useRef(false);
  useEffect(() => {
    if (prev.current === base) return;
    const was = prev.current, justSaved = saved.current, next = nextDraft(draft, was, base, justSaved);
    prev.current = base; saved.current = false;
    setDraft(next);
    setStale(next !== base && !sameDraft(was, base));
    if (next === base) setFault(null);
  }, [base, draft]);

  const parsed = fromDraft(draft, config);
  const changed = parsed.ok ? changedKeys(config, parsed.config) : [];
  const save = async () => {
    if (!parsed.ok) { setFault({ field: parsed.field, why: parsed.why }); return; }
    setBusy(true); setMsg(""); setFault(null);
    try {
      const r = await api<{ config_version: number; changed: string[]; warnings: TxtWarning[] }>(`${BASE}/${id}/v2/config`,
        { method: "PUT", body: { config: parsed.config, config_version: version } });
      setWarnings(r.warnings ?? []);
      setMsg(`Сохранено, версия ${r.config_version}: ${r.changed.join(", ") || "без изменений"}. На сайте — в течение 5 минут.`);
      saved.current = true;
      onSaved();
    } catch (e) {
      const b = e instanceof ApiException ? (e.body as unknown as { field?: string; error?: string } | null) : null;
      if (b?.field) setFault({ field: fieldOwner(b.field), why: String(b.error ?? "") });
      setMsg(failText(e, "Не удалось сохранить"));
    }
    setBusy(false);
  };
  const warnOf = (k: string) => warnings.filter((w) => fieldOwner(w.field) === k);

  return (
    <div className={css.block}>
      <div className={css.sub}>Конфиг v2 (версия {version || "— ещё не сохранён"})</div>
      {CFG_FIELDS.map((f) => {
        const bad = fault && fault.field === f.key, w = warnOf(f.key);
        return (
          <div key={f.key} className={`${css.field} ${bad ? css.fieldBad : ""}`}>
            <div className={css.fieldHead}>
              <span>{f.label}</span>
              {f.required ? <span className={css.req}>обязательно</span> : null}
              <span className={css.meta}>{f.key}{f.hint && f.kind !== "json" ? ` · ${f.hint}` : ""}</span>
              {changed.includes(f.key) ? <span className={`${css.badge} ${css.warn}`}>изменено</span> : null}
            </div>
            <Input f={f} value={draft[f.key] ?? ""} onChange={(v) => setDraft((d) => ({ ...d, [f.key]: v }))} />
            {bad ? <div className={css.err}>{fault.why}</div> : null}
            {w.map((x) => <div key={`${x.field}-${x.rule}`} className={css.warnLine}>TXT-002 {x.level === "block" ? "запрет" : "предупреждение"}: правило {x.rule} в {x.field}</div>)}
          </div>
        );
      })}
      {fault && !CFG_FIELDS.some((f) => f.key === fault.field) ? <div className={css.err}>{fault.field}: {fault.why}</div> : null}
      {msg ? <div className={css.toast} role="status">{msg}</div> : null}
      {stale && changed.length ? <div className={css.warnLine}>Конфиг на сервере изменился, а здесь — несохранённая правка: сохранение ответит «обновите». «Отменить правку» возьмёт новый конфиг.</div> : null}
      {warnings.length ? <div className={css.warnLine}>Тексты с замечаниями TXT-002: {warnings.length}. Сохранение не остановлено — поправьте, если это не намеренно.</div> : null}
      <div className={css.line}>
        <button type="button" className={css.btn} disabled={busy || (!changed.length && version > 0)} onClick={() => void save()}>Сохранить конфиг</button>
        <button type="button" className={css.ghost} disabled={busy || !changed.length} onClick={() => { setDraft(base); setFault(null); setStale(false); }}>Отменить правку</button>
        {!parsed.ok ? <span className={css.err}>{parsed.field}: {parsed.why}</span> : changed.length ? <span className={css.muted}>изменено: {changed.join(", ")}</span> : null}
      </div>
    </div>
  );
}
